import { Alert, Button, Col, Form, Input, Row, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PaymentConfigurationData } from "../../../models/PaymentMethodData";
import { CheckIsValidPaymentConfigurationName } from "../../../api/repositoryEP";
import { InfoCircleOutlined } from "@ant-design/icons";

interface PaymentsMethodFormProps {
    selectedConfiguration: PaymentConfigurationData | null;
    setSelectedConfiguration: (aad: PaymentConfigurationData | null) => void;
    create: (name: string, isDefault: boolean) => void;
    editName: (id: string, name: string, isDefault: boolean) => void;
    isLoading: boolean;
}

const PaymentsMethodForm = (props: PaymentsMethodFormProps): JSX.Element => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [disableButton, setDisableButton] = useState<boolean>(true);

    const onFinish = async (values: { name: string; isDefault: boolean }) => {
        try {
            if (props.selectedConfiguration) {
                await props.editName(props.selectedConfiguration.id, values.name, values.isDefault ?? false);
            } else {
                await props.create(values.name, values.isDefault ?? false);
            }
        } finally {
            form.setFieldValue("name", "");
        }
    };

    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    const checkIsValidName = (name: string): void => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(async () => {
            setDisableButton(true);
            form.setFields([{ name: "name", errors: [] }]);

            if (!name.trim()) {
                return;
            }

            if (
                props.selectedConfiguration &&
                props.selectedConfiguration.name.trim() === name.trim()
            ) {
                setDisableButton(false);
                return;
            }

            try {
                const response = await CheckIsValidPaymentConfigurationName(name);
                const isValid = response?.type === 1 ? response.data : false;

                if (!isValid) {
                    form.setFields([
                        {
                            name: "name",
                            errors: [t("pages.account-asignation.name-unavailable")],
                        },
                    ]);
                } else {
                    setDisableButton(false);
                }
            } catch (error) {
                form.setFields([
                    {
                        name: "name",
                        errors: [t("pages.account-asignation.api-error")],
                    },
                ]);
            }
        }, 300);

        setDebounceTimer(timer);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        form.setFields([{ name: "name", errors: [] }]);
        setDisableButton(true);
        checkIsValidName(value);
    };

    useEffect(() => {
        if (props.selectedConfiguration) {
            form.setFieldValue("name", props.selectedConfiguration.name);
            form.setFieldValue("isDefault", props.selectedConfiguration.isDefault ?? false);
        } else {
            form.resetFields();
        }
    }, [props.selectedConfiguration, form]);

    const hasCountriesOrAccounts = !!(
        (props.selectedConfiguration?.marketsSelected?.length ?? 0) > 0 ||
        (props.selectedConfiguration?.accountsSelected?.length ?? 0) > 0
    );

    return (
        <Form
            layout="vertical"
            onFinish={onFinish}
            form={form}
            initialValues={{ name: "", isDefault: false }}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Form.Item
                        label={t("pages.account-asignation.name")}
                        name="name"
                        rules={[
                            { required: true, message: t("pages.account-asignation.name-required") || "" },
                        ]}
                    >
                        <Input onChange={handleInputChange} />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item
                        label={
                            <span>
                                {t("pages.payments-configuration.is-default")}&nbsp;
                                <Tooltip title={t("pages.payments-configuration.is-default-help")}>
                                    <InfoCircleOutlined style={{ color: "#888" }} />
                                </Tooltip>
                            </span>
                        }
                        name="isDefault"
                        valuePropName="checked"
                    >
                        <Switch disabled={hasCountriesOrAccounts} />
                    </Form.Item>
                    {hasCountriesOrAccounts && (
                        <Alert
                            type="warning"
                            showIcon
                            message={t("pages.payments-configuration.is-default-disabled-reason")}
                            style={{ marginTop: "-4px", marginBottom: "8px" }}
                        />
                    )}
                </Col>
                <Col xs={24} md={24} style={{ textAlign: "right" }}>
                    <Button
                        loading={props.isLoading}
                        className="app-button"
                        disabled={props.isLoading || disableButton}
                        htmlType="submit"
                    >
                        {props.selectedConfiguration ? t("button.edit") : t("button.create")}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default PaymentsMethodForm;
